import { ProductType } from '@inter-ikea-storageone/range';
import CategoryAdditionalInlineMessage from 'components/ProductSidebar/CategoryWidget/CategoryAdditionalInlineMessage';
import CeilingHeightInfoMessage from 'components/ProductSidebar/CategoryWidget/CeilingHeightInfoMessage';
import SlidingDoorsInfoMessage from 'components/ProductSidebar/CategoryWidget/SlidingDoorsInfoMessage';
import { ProductListConfig } from 'rangeConfig/@types';
import removeAllSlidingDoorsFromScene from 'rangeConfig/pax/behaviors/removeAllSlidingDoorsFromScene';
import { matchDoorsToFrames } from 'rangeConfig/pax/filters/mainMenuProductFilters';
import {
  getCustomSlidingDoorImage,
  notCustomised,
  shouldDisableSlidingDoorProducts
} from 'rangeConfig/pax/utils/utils';
import autoFillDoors from 'rangeConfig/pax/behaviors/autoFillDoors';
import dragAndDropDoorsBehavior from 'rangeConfig/pax/behaviors/dragAndDropDoorsBehavior';
import removeAllHingedDoorsFromScene from 'rangeConfig/shared/behaviors/removeAllHingedDoorsFromScene';
import showDoors from 'rangeConfig/shared/behaviors/showDoors';
import { noProductsWithTypesInScene } from 'rangeConfig/shared/rules/noneOptionSelectedRules';
import withInlineMessage from 'rangeConfig/shared/widgets/withInlineMessage';
import addCustomSlidingDoor from 'rangeConfig/pax/behaviors/addCustomSlidingDoor';

export const hingedDoorsProductListConfig: ProductListConfig = {
  cardsTitle: 'name',
  infoButtonAriaLabel:
    'name storageone.doorName communicatedDimensionsLocalized.width communicatedDimensionsLocalized.unit storageone.productCardTextWide',
  cluster: ['type', 'name', 'typeName', 'design', 'dimensions.width'],
  behaviors: {
    productClicked: [
      showDoors,
      autoFillDoors(['S40301124_ASM', 'S10301125_ASM', 'S20414829_ASM', 'S00414830_ASM'])
    ],
    productDropped: [dragAndDropDoorsBehavior]
  },
  productFilters: [matchDoorsToFrames],
  sortOrder: [],
  categories: [
    {
      id: 'hingedDoor',
      categoryTitle: 'storageone.doorCategoryTitleHingedDoors',
      cardsSubtitle:
        'designText, communicatedDimensionsLocalized.width communicatedDimensionsLocalized.unit storageone.productCardTextWide',
      widgets: [
        {
          component: withInlineMessage(
            'storageone.additionalHingesInfo',
            true
          )(CategoryAdditionalInlineMessage)
        }
      ],
      products: [{ path: 'type', value: ProductType.PAX_HINGE_DOOR_ASM }],
      noneOption: {
        title: 'storageone.productListNoHingedDoorsOptionTitle',
        subtitle: 'storageone.productListNoHingedDoorsOptionSubtitle',
        behaviors: { removeProducts: [removeAllHingedDoorsFromScene] },
        selected: noProductsWithTypesInScene([ProductType.PAX_HINGE_DOOR_ASM])
      }
    }
  ]
};

export const slidingDoorsCustomProductListConfig: ProductListConfig = {
  cardsTitle: 'storageone.productListCustomSlidingDoorTitle',
  cardsSubtitle: 'storageone.productListCustomSlidingDoorSubtitle',
  infoButtonAriaLabel:
    'name storageone.doorCategoryTitleSlidingDoors communicatedDimensionsLocalized.width communicatedDimensionsLocalized.unit storageone.productCardTextWide',
  hideInfoButton: true,
  hidePrice: true,
  hideAvailability: true,
  productCardMainImage: getCustomSlidingDoorImage,
  productCardHoverImage: () => undefined,
  cluster: ['type', 'name', 'typeName', 'design'],
  behaviors: {
    productClicked: [showDoors, addCustomSlidingDoor],
    productDropped: [showDoors, addCustomSlidingDoor]
  },
  productFilters: [matchDoorsToFrames],
  sortOrder: [],
  categories: [
    {
      id: 'slidingDoor',
      categoryTitle: 'storageone.doorCategoryTitleSlidingDoors',
      widgets: [
        { component: SlidingDoorsInfoMessage, alwaysShow: true },
        { component: CeilingHeightInfoMessage }
      ],
      products: [
        { path: 'coreProductId', value: 'S59560297' },
        { path: 'coreProductId', value: 'S39560302' },
        { path: 'coreProductId', value: 'S49560306' },
        { path: 'coreProductId', value: 'S69560310' },
        { path: 'coreProductId', value: 'S79437980' },
        { path: 'coreProductId', value: 'S09437988' },
        { path: 'coreProductId', value: 'S59437995' },
        { path: 'coreProductId', value: 'S79438003' }
      ],
      shouldDisableProducts: shouldDisableSlidingDoorProducts,
      noneOption: {
        title: 'storageone.productListNoSlidingDoorsOptionTitle',
        subtitle: 'storageone.productListNoSlidingDoorsOptionSubtitle',
        behaviors: { removeProducts: [removeAllSlidingDoorsFromScene] },
        selected: noProductsWithTypesInScene([
          ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
          ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
        ])
      },
      noSkipButton: true
    }
  ]
};

export const slidingDoorsDesktopProductListConfig: ProductListConfig = {
  cardsTitle: 'name',
  cardsSubtitle:
    'designText, communicatedDimensionsLocalized.width communicatedDimensionsLocalized.unit storageone.productCardTextWide',
  cluster: ['type', 'name', 'typeName', 'design'],
  behaviors: {
    productClicked: [
      showDoors,
      autoFillDoors(['S40301124_ASM', 'S10301125_ASM', 'S20414829_ASM', 'S00414830_ASM'])
    ],
    productDropped: [dragAndDropDoorsBehavior]
  },
  productFilters: [matchDoorsToFrames],
  selected: notCustomised,
  sortOrder: [],
  categories: [
    {
      id: 'slidingDoor',
      products: [{ path: 'type', value: ProductType.PAX_SLIDING_DOOR_REGULAR_ASM }],
      shouldDisableProducts: shouldDisableSlidingDoorProducts
    },
    {
      id: 'slidingDoor',
      products: [{ path: 'type', value: ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM }],
      shouldDisableProducts: shouldDisableSlidingDoorProducts,
      noSkipButton: true
    }
  ]
};

export const slidingDoorsMobileProductListConfig: ProductListConfig = {
  cardsTitle: 'name',
  cardsSubtitle:
    'designText, communicatedDimensionsLocalized.width communicatedDimensionsLocalized.unit storageone.productCardTextWide',
  cluster: ['type', 'name', 'typeName', 'design'],
  behaviors: {
    productClicked: [
      showDoors,
      autoFillDoors(['S40301124_ASM', 'S10301125_ASM', 'S20414829_ASM', 'S00414830_ASM'])
    ],
    productDropped: [dragAndDropDoorsBehavior]
  },
  productFilters: [matchDoorsToFrames],
  sortOrder: [],
  categories: [
    {
      id: 'slidingDoor',
      products: [{ path: 'type', value: ProductType.PAX_SLIDING_DOOR_REGULAR_ASM }],
      shouldDisableProducts: shouldDisableSlidingDoorProducts,
      widgets: [
        { component: SlidingDoorsInfoMessage, alwaysShow: true },
        { component: CeilingHeightInfoMessage }
      ],
      noneOption: {
        title: 'storageone.productListNoSlidingDoorsOptionTitle',
        subtitle: 'storageone.productListNoSlidingDoorsOptionSubtitle',
        behaviors: { removeProducts: [removeAllSlidingDoorsFromScene] },
        selected: noProductsWithTypesInScene([
          ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
          ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
        ])
      }
    },
    {
      id: 'slidingDoor',
      products: [{ path: 'type', value: ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM }],
      shouldDisableProducts: shouldDisableSlidingDoorProducts
    }
  ]
};
